import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { inr, marginTone } from "./_shared";

/** Per-project Margin roll-up — Super Admin / Accounts only. */
export default function ProjectMarginCard({ p, user }) {
  const role = user?.role;
  const canView = ["super_admin", "accounts"].includes(role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/costing/margin/${p.id}`);
        if (!cancelled) setData(data);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, []);
  if (!canView) return null;

  const tone = data ? marginTone(data.margin_percent) : null;

  return (
    <div className="hk-card p-4" data-testid="project-margin-card">
      <div className="flex items-center justify-between mb-2">
        <div className="hk-tiny-label">Margin summary</div>
        <Link to="/costing"
          className="text-[10px] font-bold uppercase text-orange-700 hover:text-orange-900">
          Full dashboard →
        </Link>
      </div>
      {loading || !data ? (
        <div className="text-xs text-slate-400">Loading…</div>
      ) : (
        <>
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-xs text-slate-500">Quote value</span>
            <span className="font-mono font-bold text-slate-900" data-testid="pm-quote">
              {inr(data.quote_value, 0)}
            </span>
          </div>
          <div className="mt-2 space-y-1 text-xs">
            <Row label="Sheets" value={inr(data.sheet_cost, 0)}
              detail={data.sheet_weight_kg > 0 ? `${data.sheet_weight_kg} kg` : null} />
            <Row label="Purchase orders" value={inr(data.po_cost, 0)}
              detail={data.counts?.purchase_orders > 0 ? `${data.counts.purchase_orders} POs` : null} />
            <Row label="Labour" value={inr(data.labour_cost, 0)}
              detail={data.labour_hours > 0 ? `${data.labour_hours} hrs` : null} />
            <Row label="Transport" value={inr(data.transport_cost, 0)} />
            <Row label="Installation" value={inr(data.installation_cost, 0)} />
          </div>
          <div className="mt-2 pt-2 border-t-2 border-slate-200 flex items-baseline justify-between text-xs">
            <span className="font-bold text-slate-700">Total spend</span>
            <span className="font-mono font-black text-slate-900" data-testid="pm-total-cost">
              {inr(data.total_cost, 0)}
            </span>
          </div>
          <div className="mt-2 rounded-sm p-2" style={{ background: tone.bg }}>
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] font-bold uppercase" style={{ color: tone.color }}>
                Margin · {tone.label}
              </span>
              <span className="text-lg font-black font-mono" style={{ color: tone.color }}
                data-testid="pm-margin-amount">
                {inr(data.margin_amount, 0)}
              </span>
            </div>
            <div className="text-right text-[11px] font-bold font-mono"
              style={{ color: tone.color }}
              data-testid="pm-margin-percent">
              {data.margin_percent.toFixed(1)}%
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value, detail }) {
  return (
    <div className="flex items-baseline justify-between text-slate-600">
      <span>
        {label}
        {detail && <span className="text-[10px] text-slate-400"> · {detail}</span>}
      </span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
